import { TimespanSchema } from '@amzn/pi-clarity-common/timespan/timespan-config';
import {
  DEFAULT_TIMEZONE,
  formatTimezoneName,
  getCurrentTimezoneInfo,
} from '@clarity-website/config/timezoneConfig';
import { TimespanInterface } from '@clarity-website/reports/filters/filter-types';
import { format, utcToZonedTime } from 'date-fns-tz';

export function extractTimezoneFromTimespans(
  selectedTimespans?: TimespanSchema[],
): string {
  if (!selectedTimespans || selectedTimespans.length === 0) {
    return DEFAULT_TIMEZONE;
  }

  // Look through all timespans for a timezone adjuster
  for (const timespan of selectedTimespans) {
    if (timespan.temporalAdjusters) {
      const timezoneAdjuster = timespan.temporalAdjusters.find(
        (adjuster) => adjuster.function === 'adjustTimezone',
      );

      if (
        timezoneAdjuster &&
        timezoneAdjuster.arguments &&
        timezoneAdjuster.arguments.length > 0
      ) {
        const timezone = timezoneAdjuster.arguments[0];
        return timezone || DEFAULT_TIMEZONE;
      }
    }
  }

  return DEFAULT_TIMEZONE;
}

export function updateTimespanWithTimezone<
  T extends TimespanSchema | TimespanInterface,
>(timespan: T, timezone: string): T {
  const effectiveTimezone =
    timezone && timezone.trim() !== '' ? timezone : DEFAULT_TIMEZONE;

  // If no temporal adjusters exist, just return the timespan as is
  if (!timespan.temporalAdjusters) {
    return timespan;
  }

  const timezoneAdjusterIndex = timespan.temporalAdjusters.findIndex(
    (adjuster) => adjuster.function === 'adjustTimezone',
  );

  // If no timezone adjuster exists, don't modify the timespan
  if (timezoneAdjusterIndex === -1) {
    return timespan;
  }

  // Update the existing timezone adjuster
  const updatedAdjusters = [...timespan.temporalAdjusters];
  updatedAdjusters[timezoneAdjusterIndex] = {
    ...updatedAdjusters[timezoneAdjusterIndex],
    arguments: [effectiveTimezone],
  };

  return {
    ...timespan,
    temporalAdjusters: updatedAdjusters,
  } as T;
}

export function formatTimeInTimezone(timezone: string): string {
  const effectiveTimezone =
    timezone && timezone.trim() !== '' ? timezone : DEFAULT_TIMEZONE;
  const utcDate = new Date();

  const zonedDate = utcToZonedTime(utcDate, effectiveTimezone);

  // Format time as "4:15PM" in the specified timezone
  const formattedTime = format(zonedDate, 'h:mma');

  const { abbreviation } = getCurrentTimezoneInfo(effectiveTimezone);
  const tzDisplay = abbreviation;

  return `${formattedTime} ${tzDisplay}`;
}

export function filterTimezones(
  timezones: string[],
  searchQuery: string,
): string[] {
  if (!searchQuery) return timezones;

  const query = searchQuery.toLowerCase();
  return timezones.filter((timezone) => {
    const formattedName = formatTimezoneName(timezone).toLowerCase();
    return (
      formattedName.includes(query) || timezone.toLowerCase().includes(query)
    );
  });
}
